import React, { useEffect, useMemo, useState } from "react";
import { ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react";

/**
 * Lightweight sortable + filterable table.
 * columns: [{ key, label, render?, sortable?, accessor? }]
 */
export default function DataTable({ data = [], columns, searchKeys = [], testid = "data-table", emptyMessage = "No records" }) {
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("asc");

  useEffect(() => {
    if (!sortKey && columns?.length) {
      // no-op
    }
  }, [columns, sortKey]);

  const filtered = useMemo(() => {
    if (!search.trim()) return data;
    const q = search.toLowerCase();
    return data.filter((row) =>
      (searchKeys.length ? searchKeys : Object.keys(row)).some((k) => {
        const v = row[k];
        return v != null && String(v).toLowerCase().includes(q);
      })
    );
  }, [data, search, searchKeys]);

  const sorted = useMemo(() => {
    if (!sortKey) return filtered;
    const arr = [...filtered];
    arr.sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === "number" && typeof bv === "number") {
        return sortDir === "asc" ? av - bv : bv - av;
      }
      return sortDir === "asc"
        ? String(av).localeCompare(String(bv))
        : String(bv).localeCompare(String(av));
    });
    return arr;
  }, [filtered, sortKey, sortDir]);

  const toggleSort = (k) => {
    if (sortKey === k) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortKey(k);
      setSortDir("asc");
    }
  };

  return (
    <div data-testid={testid}>
      <div className="flex items-center justify-between mb-3 gap-3">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Filter…"
          className="w-full max-w-xs px-3 py-2 text-sm bg-white border border-brand rounded-md"
          data-testid={`${testid}-search`}
        />
        <div className="text-xs text-brand-muted">{sorted.length} record{sorted.length !== 1 ? "s" : ""}</div>
      </div>
      <div className="overflow-x-auto rounded-xl border border-brand bg-white">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="text-left">
              {columns.map((c) => (
                <th
                  key={c.key}
                  className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-brand-muted border-b border-brand cursor-pointer select-none"
                  onClick={() => c.sortable !== false && toggleSort(c.key)}
                >
                  <span className="inline-flex items-center gap-1">
                    {c.label}
                    {c.sortable !== false && (
                      sortKey === c.key
                        ? (sortDir === "asc" ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />)
                        : <ArrowUpDown className="w-3 h-3 opacity-40" />
                    )}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.length === 0 ? (
              <tr><td colSpan={columns.length} className="px-4 py-10 text-center text-sm text-brand-muted">{emptyMessage}</td></tr>
            ) : (
              sorted.map((row, idx) => (
                <tr key={row.id || idx} className="hover:bg-[var(--brand-bg)] border-b border-brand last:border-0">
                  {columns.map((c) => (
                    <td key={c.key} className="px-4 py-3 text-brand-text align-middle">
                      {c.render ? c.render(row) : (row[c.key] ?? "—")}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
